import {
  ArrowRight,
  BookOpen,
  CalendarCheck,
  ChartNoAxesCombined,
  CheckCheck,
  History,
  Inbox,
  Kanban,
  ListChecks,
  MessageSquare,
  Plug,
  ReceiptIndianRupee,
  Repeat2,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Target,
  UserRoundCheck,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { motion } from 'motion/react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { duration, easing, revealViewport } from '../motion/tokens';

/* ------------------------------------------------------------------ *
 * Icons
 * ------------------------------------------------------------------ */

const MARKETING_ICONS: Record<string, LucideIcon> = {
  sparkles: Sparkles,
  inbox: Inbox,
  kanban: Kanban,
  book: BookOpen,
  repeat: Repeat2,
  calendar: CalendarCheck,
  receipt: ReceiptIndianRupee,
  chart: ChartNoAxesCombined,
  handoff: UserRoundCheck,
  users: Users,
  plug: Plug,
  sliders: SlidersHorizontal,
  message: MessageSquare,
  target: Target,
  history: History,
  check: CheckCheck,
  shield: ShieldCheck,
  list: ListChecks,
};

export function MkIcon({ name, className }: { name: string; className?: string }): JSX.Element {
  const Icon = MARKETING_ICONS[name] ?? Sparkles;
  return <Icon className={className} strokeWidth={1.6} aria-hidden />;
}

/**
 * WhatsApp and Instagram glyphs are drawn inline — lucide dropped brand icons,
 * and these are the two channels the product is defined by.
 */
export function WhatsAppGlyph({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
      aria-hidden
      focusable="false"
    >
      <path d="M12.04 2C6.6 2 2.2 6.4 2.2 11.84c0 1.9.53 3.68 1.46 5.2L2 22l5.1-1.62a9.8 9.8 0 0 0 4.94 1.32h.01c5.43 0 9.84-4.4 9.84-9.84C21.89 6.4 17.48 2 12.04 2Zm5.72 13.9c-.24.68-1.4 1.3-1.93 1.34-.5.05-.97.23-3.27-.68-2.76-1.09-4.5-3.9-4.64-4.08-.13-.18-1.1-1.46-1.1-2.8s.7-1.98.95-2.25c.24-.27.53-.34.7-.34h.5c.16 0 .38-.06.6.45.23.55.77 1.9.84 2.04.07.13.11.29.02.47-.09.18-.13.29-.27.45l-.4.46c-.13.13-.27.28-.11.55.15.27.68 1.13 1.47 1.83 1.01.9 1.86 1.18 2.13 1.31.27.14.42.12.58-.07.16-.18.67-.78.85-1.05.18-.27.36-.22.6-.13.25.09 1.58.74 1.85.88.27.13.45.2.52.31.07.11.07.63-.17 1.31Z" />
    </svg>
  );
}

export function InstagramGlyph({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      aria-hidden
      focusable="false"
    >
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="3.8" />
      <circle cx="17.2" cy="6.8" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  );
}

/* ------------------------------------------------------------------ *
 * Layout
 * ------------------------------------------------------------------ */

export function MkContainer({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return <div className={`mx-auto w-full max-w-7xl px-5 sm:px-8 ${className}`}>{children}</div>;
}

export function MkEyebrow({ children }: { children: ReactNode }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-[var(--mk-line)] bg-[var(--mk-surface)] px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--mk-ink-soft)]">
      <span
        className="h-1.5 w-1.5 rounded-full bg-[var(--mk-cyan)] shadow-[0_0_8px_var(--mk-cyan)]"
        aria-hidden
      />
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Motion
 * ------------------------------------------------------------------ */

/**
 * Fade-and-lift on first scroll into view. MotionProvider is configured with
 * `reducedMotion="user"`, so the transform is dropped automatically when the
 * visitor prefers reduced motion and only the fade remains.
 */
export function MkReveal({
  children,
  delay = 0,
  className,
  as = 'div',
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
  as?: 'div' | 'li';
}): JSX.Element {
  const Component = as === 'li' ? motion.li : motion.div;
  return (
    <Component
      className={className}
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={revealViewport}
      transition={{ duration: duration.section, ease: easing.entrance, delay }}
    >
      {children}
    </Component>
  );
}

export function MkSectionHeading({
  id,
  eyebrow,
  heading,
  body,
  align = 'center',
}: {
  id?: string;
  eyebrow?: string;
  heading: string;
  body?: string;
  align?: 'center' | 'left';
}): JSX.Element {
  const alignment =
    align === 'center' ? 'mx-auto max-w-3xl items-center text-center' : 'max-w-3xl items-start';
  return (
    <MkReveal className={`flex flex-col gap-5 ${alignment}`}>
      {eyebrow ? <MkEyebrow>{eyebrow}</MkEyebrow> : null}
      <h2 id={id} className="mk-h2 text-[var(--mk-ink)]">
        {heading}
      </h2>
      {body ? <p className="text-lg leading-relaxed text-[var(--mk-ink-soft)]">{body}</p> : null}
    </MkReveal>
  );
}

/* ------------------------------------------------------------------ *
 * Actions
 * ------------------------------------------------------------------ */

type ButtonVariant = 'primary' | 'secondary' | 'quiet';

const BUTTON_BASE =
  'group inline-flex items-center justify-center gap-2 rounded-full text-sm font-semibold transition-all duration-200 disabled:opacity-50';

const BUTTON_STYLES: Record<ButtonVariant, string> = {
  primary:
    'bg-[linear-gradient(120deg,var(--mk-blue),var(--mk-violet))] px-6 py-3 text-white shadow-[0_14px_40px_-16px_rgba(120,90,255,0.9)] hover:brightness-115',
  secondary:
    'border border-[var(--mk-line-strong)] bg-[var(--mk-surface)] px-6 py-3 text-[var(--mk-ink)] hover:border-[var(--mk-cyan)] hover:bg-[var(--mk-surface-strong)]',
  quiet: 'px-3 py-2 text-[var(--mk-ink-soft)] hover:text-[var(--mk-ink)]',
};

function isRouterLink(href: string): boolean {
  return href.startsWith('/') && !href.startsWith('//');
}

/**
 * Renders as a router link, an anchor jump, or a full page load as appropriate.
 *
 * Absolute URLs stay in the same tab by default: the ones we use point at the
 * Vaanexa app on its own subdomain, and forcing a new tab on a sign-in link is
 * hostile. Pass `newTab` for genuinely outbound destinations.
 */
export function MkLink({
  href,
  className,
  children,
  onClick,
  newTab = false,
}: {
  href: string;
  className?: string;
  children: ReactNode;
  onClick?: () => void;
  newTab?: boolean;
}): JSX.Element {
  if (isRouterLink(href)) {
    return (
      <Link to={href} className={className} onClick={onClick}>
        {children}
      </Link>
    );
  }
  return (
    <a
      href={href}
      className={className}
      onClick={onClick}
      target={newTab ? '_blank' : undefined}
      rel={newTab ? 'noreferrer' : undefined}
    >
      {children}
    </a>
  );
}

export function MkButton({
  href,
  children,
  variant = 'primary',
  withArrow = false,
  className = '',
  onClick,
}: {
  href: string;
  children: ReactNode;
  variant?: ButtonVariant;
  withArrow?: boolean;
  className?: string;
  onClick?: () => void;
}): JSX.Element {
  return (
    <MkLink
      href={href}
      onClick={onClick}
      className={`${BUTTON_BASE} ${BUTTON_STYLES[variant]} ${className}`}
    >
      {children}
      {withArrow ? (
        <ArrowRight
          className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
          aria-hidden
        />
      ) : null}
    </MkLink>
  );
}

/* ------------------------------------------------------------------ *
 * Surfaces
 * ------------------------------------------------------------------ */

export function MkPanel({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return <div className={`mk-panel rounded-2xl ${className}`}>{children}</div>;
}

/** Small pill used for statuses inside product mock-ups. */
export function MkPill({
  children,
  tone = 'default',
  className = '',
}: {
  children: ReactNode;
  tone?: 'default' | 'positive' | 'accent' | 'warn';
  className?: string;
}): JSX.Element {
  const tones = {
    default: 'border-[var(--mk-line)] bg-[var(--mk-surface)] text-[var(--mk-ink-soft)]',
    positive:
      'border-[color-mix(in_srgb,var(--mk-positive)_35%,transparent)] bg-[color-mix(in_srgb,var(--mk-positive)_12%,transparent)] text-[var(--mk-positive)]',
    accent:
      'border-[color-mix(in_srgb,var(--mk-violet)_40%,transparent)] bg-[color-mix(in_srgb,var(--mk-violet)_14%,transparent)] text-[#c4b0ff]',
    warn: 'border-[color-mix(in_srgb,var(--mk-warn)_35%,transparent)] bg-[color-mix(in_srgb,var(--mk-warn)_12%,transparent)] text-[var(--mk-warn)]',
  } as const;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${tones[tone]} ${className}`}
    >
      {children}
    </span>
  );
}

/** Shared ambient background: grid, one soft light, grain. */
export function MkAmbience({
  variant = 'primary',
  className = '',
}: {
  variant?: 'primary' | 'cool' | 'cyan';
  className?: string;
}): JSX.Element {
  const glow =
    variant === 'cool' ? 'mk-glow-cool' : variant === 'cyan' ? 'mk-glow-cyan' : 'mk-glow-primary';
  return (
    <div
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
      aria-hidden
    >
      <div className="absolute inset-0 mk-grid" />
      <div className={`absolute -top-40 left-1/2 h-[520px] w-[820px] -translate-x-1/2 ${glow}`} />
      <div className="absolute inset-0 mk-noise" />
    </div>
  );
}
